import { AccessState } from 'src/app/services/access.service'
import { JobDto, JobStatus } from 'src/app/services/dtos'

// Drives the patched apiService.getJob to return a deterministic status sequence
// so reviewers can step through every gating outcome the production components handle.
export type StacksetsJobScenarioKey =
  | 'none'
  | 'instant_done'
  | 'running_then_done'
  | 'running_then_failed'
  | 'running_timeout'
  | 'http_error'
  | 'skipped'

export interface StacksetsJobScenario {
  key: StacksetsJobScenarioKey
  label: string
  description: string
}

export const STACKSETS_JOB_SCENARIOS: ReadonlyArray<StacksetsJobScenario> = [
  {
    key: 'none',
    label: 'No job',
    description: 'Backend returned no jobId — components render normally with no gating.',
  },
  {
    key: 'instant_done',
    label: 'Instant done',
    description: 'Cache hit — first poll returns done. Spinner may flash briefly.',
  },
  {
    key: 'running_then_done',
    label: 'Running → done',
    description: 'Polls return running for ~10s, then done. Typical happy path.',
  },
  {
    key: 'running_then_failed',
    label: 'Failed',
    description: 'First poll returns failed. UI unblocks immediately and a "Template preparation failed" toast fires.',
  },
  {
    key: 'running_timeout',
    label: 'Simulated timeout',
    description:
      'Polls keep returning running. Client gives up after 1s (production: 120s), unblocks, and toasts "Templates may still be generating".',
  },
  {
    key: 'http_error',
    label: 'HTTP error',
    description: 'getJob throws — handleArpioError fires (toast or 401 redirect).',
  },
  {
    key: 'skipped',
    label: 'Skipped',
    description: 'Terminal status of skipped — degenerate case, treated as success.',
  },
]

// Per-scenario static job IDs so flipping scenarios is observable to ngOnChanges
// in the showcased components (the input value must actually change to retrigger polling).
export const STACKSETS_JOB_IDS: Record<StacksetsJobScenarioKey, string | null> = {
  none: null,
  instant_done: 'mock-job-instant',
  running_then_done: 'mock-job-running-done',
  running_then_failed: 'mock-job-running-failed',
  running_timeout: 'mock-job-timeout',
  http_error: 'mock-job-http-error',
  skipped: 'mock-job-skipped',
}

export function buildMockJobDto(jobId: string, status: JobStatus, accountId = 'MOCK-TEST-account-id'): JobDto {
  return {
    jobId,
    accountId,
    status,
    trigger: 'automatic',
    startTime: new Date().toISOString(),
    jobType: 'stackSetTemplateRegeneration',
  }
}

// ──────────────────────────────────────────────────────────────────────────
//  StackSets UI scenario presets
// ──────────────────────────────────────────────────────────────────────────
//
// Each preset seeds the @Input set on either <stacksets-instructions> or
// <stacksets-update-instructions> to produce one specific UI variant. The
// regen-job axis (STACKSETS_JOB_SCENARIOS above) is independent.

export type StacksetsFlow = 'setup' | 'update'

export interface StacksetsScenarioInputs {
  // setup-flow inputs
  migrationMode?: boolean
  sourceManagedByStackSet?: boolean
  targetManagedByStackSet?: boolean
  sourceStackName?: string
  targetStackName?: string
  testingSource?: boolean
  testingTarget?: boolean
  sourceError?: boolean
  targetError?: boolean
  sourceTestCount?: number
  targetTestCount?: number
  // Access state — drives the setup flow's per-side badge as well as the update flow
  // (also reuses sourceTestCount / targetTestCount above)
  sourceAccessState?: AccessState
  targetAccessState?: AccessState
}

export interface StacksetsScenarioPreset {
  key: string
  flow: StacksetsFlow
  group: string
  label: string
  description: string
  inputs: StacksetsScenarioInputs
}

export const STACKSETS_SCENARIO_PRESETS: ReadonlyArray<StacksetsScenarioPreset> = [
  // ── Setup flow — Create flow ─────────────────────────────────────────
  {
    key: 'setup-create-fresh',
    flow: 'setup',
    group: 'Create flow',
    label: 'Fresh — neither managed',
    description: 'Brand-new app. Both source and target need StackSets onboarding. No CFN stacks present.',
    inputs: { migrationMode: false, sourceManagedByStackSet: false, targetManagedByStackSet: false },
  },
  {
    key: 'setup-create-cfn-source',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — source',
    description: 'Source has a legacy CFN access stack but is not yet managed by StackSets — top-level warning fires.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
    },
  },
  {
    key: 'setup-create-cfn-target',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — target',
    description: 'Target has a legacy CFN access stack but is not yet managed by StackSets.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-create-cfn-both',
    flow: 'setup',
    group: 'Create flow',
    label: 'CFN conflict — both',
    description: 'Both source and target have legacy CFN access stacks — plural-account messaging.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-create-source-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Source configured, target pending',
    description: 'Source pair is managed; target still needs its StackSets deploy.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: false,
      sourceAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'setup-create-target-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Target configured, source pending',
    description: 'Target pair is managed; source still needs its StackSets deploy.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: true,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'setup-create-source-stale',
    flow: 'setup',
    group: 'Create flow',
    label: 'Source managed but out of date',
    description:
      'Source is StackSet-managed on an older template — grey "Out of date" badge plus the update-stack-set command and its TEST ACCESS button.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.UPDATE_TEMPLATE,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'setup-create-both-configured',
    flow: 'setup',
    group: 'Create flow',
    label: 'Both configured',
    description: 'Setup is complete in create flow — both cards show the green "Access configured" badge.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.SUCCESS,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  // ── Setup flow — Test access states ─────────────────────────────────
  {
    key: 'setup-test-source-progress',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — testing in progress',
    description: 'Source TEST ACCESS button shows the spinner + TESTING… disabled state.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      testingSource: true,
    },
  },
  {
    key: 'setup-test-source-error-1',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — error (1 retry)',
    description: 'After one failed test, the "It\'s still not working." retry message appears above the button.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceError: true,
      sourceTestCount: 1,
    },
  },
  {
    key: 'setup-test-source-error-2',
    flow: 'setup',
    group: 'Test access states',
    label: 'Source — error (2 retries)',
    description: 'After two failed tests, the alternate retry message ("Hmm... It\'s not working yet.") shows.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceError: true,
      sourceTestCount: 2,
    },
  },
  {
    key: 'setup-test-target-progress',
    flow: 'setup',
    group: 'Test access states',
    label: 'Target — testing in progress',
    description: 'Target TEST ACCESS button shows the spinner + TESTING… disabled state.',
    inputs: {
      migrationMode: false,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      testingTarget: true,
    },
  },
  // ── Setup flow — Migration ───────────────────────────────────────────
  {
    key: 'setup-migration-pre',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — starting',
    description:
      'Migration mode with both source and target still on CFN — the migration intent intro shows, no cleanup yet.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: false,
      targetManagedByStackSet: false,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  {
    key: 'setup-migration-source-done',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — source migrated',
    description: 'Source flipped to StackSets but target still on CFN — partial migration state.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: false,
      targetStackName: 'arpio-target-access-app',
      sourceAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'setup-migration-complete',
    flow: 'setup',
    group: 'Migration flow',
    label: 'Migration — complete',
    description: 'Both pairs migrated — Steps 1-3 hide and the success banner + delete-stack cleanup commands show.',
    inputs: {
      migrationMode: true,
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceStackName: 'arpio-source-access-app',
      targetStackName: 'arpio-target-access-app',
    },
  },
  // ── Update flow — Setup mode (no StackSets configured yet) ───────────
  {
    key: 'update-setup-mode-fresh',
    flow: 'update',
    group: 'Update flow — setup mode',
    label: 'Setup mode — fresh',
    description:
      'Update component in setup mode (neither managed). Header reads "Set up StackSets"; commands say "Create".',
    inputs: { sourceManagedByStackSet: false, targetManagedByStackSet: false },
  },
  // ── Update flow — Update mode ────────────────────────────────────────
  {
    key: 'update-mode-fresh',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Update mode — fresh',
    description: 'Both managed, neither tested yet. Header reads "Update StackSets"; commands say "Update".',
    inputs: { sourceManagedByStackSet: true, targetManagedByStackSet: true },
  },
  {
    key: 'update-mode-source-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source up to date',
    description: 'Primary access shows the green "up to date" notice; recovery still needs verification.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-target-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Target up to date',
    description: 'Recovery access shows the green "up to date" notice; primary still needs verification.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-both-success',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Both up to date',
    description: 'Both pairs show "Access is up to date" — success terminal state for the update flow.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.SUCCESS,
      targetAccessState: AccessState.SUCCESS,
    },
  },
  {
    key: 'update-mode-source-testing',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — testing',
    description: 'Primary TEST ACCESS button shows TESTING… spinner.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.TESTING,
    },
  },
  {
    key: 'update-mode-source-retry-1',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — retry (1)',
    description: 'After one failed test, the "It\'s still not working." retry message renders above the button.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.FAILED,
      sourceTestCount: 1,
    },
  },
  {
    key: 'update-mode-source-retry-2',
    flow: 'update',
    group: 'Update flow — update mode',
    label: 'Source — retry (2)',
    description: 'After two failed tests, the alternate retry message renders.',
    inputs: {
      sourceManagedByStackSet: true,
      targetManagedByStackSet: true,
      sourceAccessState: AccessState.FAILED,
      sourceTestCount: 2,
    },
  },
]

// ──────────────────────────────────────────────────────────────────────────
//  RPO chart fixtures
// ──────────────────────────────────────────────────────────────────────────
